import React, { useMemo, useRef } from 'react';
import cn from 'clsx';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { Swiper, SwiperSlide } from '../swiper';
import { productCardsListSlide, productCardsListSwiper } from './product-cards-list-styles';
import { ProductCardStructure } from './product-cards-list-types';

type ProductCardsListProps<T> = {
  elementBlock: string;
  productList: T[];
  renderProductCard: (product: T) => React.ReactNode;
  sectionTitle: string;
  swiperSlideClassName?: string;
};

export const ProductCardsList = <T extends ProductCardStructure>({
  elementBlock,
  productList,
  renderProductCard,
  sectionTitle,
  swiperSlideClassName,
}: ProductCardsListProps<T>) => {
  const productRefs = useRef<HTMLDivElement[]>([]);

  const promotions = useMemo(
    () =>
      productList.map(({ name }, index: number) => ({
        name: `${elementBlock} / ${sectionTitle}`,
        creative: name,
        position: (index + 1).toString(),
      })),
    [productList, elementBlock, sectionTitle]
  );

  usePromoViewEvent(productRefs, `${elementBlock} / ${sectionTitle}`, promotions);

  return (
    <Swiper
      breakpoints={{
        320: { enabled: true },
        728: { enabled: false },
      }}
      className={productCardsListSwiper}
      slidesPerView="auto"
      watchOverflow>
      {productList.map((product: T, index) => (
        <SwiperSlide
          className={cn(productCardsListSlide, swiperSlideClassName)}
          key={createElementKey('product-card', index)}>
          <div
            data-position={index}
            ref={(el: HTMLDivElement | null) => {
              if (el && productRefs) productRefs.current[index] = el;
            }}>
            {renderProductCard(product)}
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

ProductCardsList.defaultProps = {
  swiperSlideClassName: '',
};
